<script setup lang="ts">
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { usePrinterStore } from '@/stores/printer';
import { notify } from '@/utils/notification';
import { isPlatform } from '@ionic/vue';
import { cashdrawer } from '@posx/imin-cashdrawer';
import { EscPosPrinter } from '@posx/core';

const { t } = useI18n();

// Epson standard cash drawer open command
const DEFAULT_COMMAND = '27, 112, 48, 55, 121';
const testing = ref(false);

const printerStore = usePrinterStore();

// Get form data
const $props = defineProps<{
  rootFormData?: any;
  value?: string;
}>();

// Emit for v-model binding
const $emit = defineEmits<{
  'update:value': [value: string];
}>();

// Check if it's receipt printer mode
const isReceiptPrinterMode = computed(() => {
  const connectionMode = $props.rootFormData?.connection_mode;
  return connectionMode !== 'via_imin_pos';
});

// Convert decimal comma-separated string to byte array
const decimalToBytes = (decimal: string): number[] => {
  return decimal.split(',')
    .filter(d => d.trim())
    .map(d => parseInt(d.trim(), 10))
    .filter(b => !isNaN(b) && b >= 0 && b <= 255);
};

// Test cash drawer opening
const testCashDrawer = async () => {
  if (testing.value) return;
  
  try {
    testing.value = true;

    // IMIN POS mode
    if (!isReceiptPrinterMode.value) {
      await cashdrawer.open();
      notify.success(t('cashDrawerTest.iminPosSuccess'));
      return;
    }

    // Receipt printer mode - use kick_code from config
    const kickCode = $props.rootFormData?.kick_code || DEFAULT_COMMAND;
    const bytes = decimalToBytes(kickCode);
    if (bytes.length === 0) {
      notify.error(t('cashDrawerTest.invalidCommand'));
      return;
    }

    if (isPlatform('electron')) {
      // Get default receipt printer
      const printerService = await printerStore.getService();
      const receiptPrinter = await printerService.getDefaultReceiptPrinter();
      
      if (!receiptPrinter) {
        notify.error(t('cashDrawerTest.printerNotFound'));
        return;
      }

      const byteArray = new Uint8Array(bytes);

      // Handle different connection types
      if (receiptPrinter.connection_type === 'WINDOWS_DRIVER') {
        // Windows Driver connection - use node-printer
        if (!receiptPrinter.driver_printer_name) {
          notify.error(t('cashDrawerTest.driverNotConfigured'));
          return;
        }
        
        await window.electronPrinter.directPrint(
          receiptPrinter.driver_printer_name, 
          byteArray
        );
        notify.success(t('cashDrawerTest.commandSent', { name: receiptPrinter.name }));
        
      } else if (receiptPrinter.connection_type === 'ETHERNET') {
        // Ethernet connection - use EscPosPrinter with TCP
        if (!receiptPrinter.ip_address) {
          notify.error(t('cashDrawerTest.ipNotConfigured'));
          return;
        }
        
        const escPosPrinter = new EscPosPrinter();
        const connResult = await escPosPrinter.connect(receiptPrinter.ip_address);
        
        if (!connResult) {
          notify.error(t('cashDrawerTest.connectionFailed', { ip: receiptPrinter.ip_address }));
          return;
        }
        
        if (escPosPrinter.socket.state !== 2) {
          notify.error(t('cashDrawerTest.connectionNotReady'));
          return;
        }
        
        const printResult = await escPosPrinter.write(byteArray);
        await escPosPrinter.disconnect();
        
        if (printResult) notify.success(t('cashDrawerTest.commandSent', { name: `${receiptPrinter.name} (${receiptPrinter.ip_address})` }));
        else notify.error(t('cashDrawerTest.commandFailed'));

      } else notify.error(t('cashDrawerTest.unsupportedConnection', { type: receiptPrinter.connection_type }));
    } else notify.warning(t('cashDrawerTest.platformNotSupported'));
  } catch (error: any) {
    console.error('Cash drawer test failed:', error);
    notify.error(t('cashDrawerTest.testFailed', { error: error.message || 'Unknown error' }));
  } finally {
    testing.value = false;
  }
};

const resetCommand = () => {
  if (!$props.rootFormData) return;
  $props.rootFormData.kick_code = DEFAULT_COMMAND;
  notify.info(t('cashDrawerTest.resetSuccess'));
};
</script>

<template>
  <div class="cash-drawer-test">
    <div class="flex gap-2">
      <el-button 
        type="primary" 
        size="small"
        :loading="testing"
        @click="testCashDrawer"
      >
        {{ testing ? $t('cashDrawerTest.testing') : $t('cashDrawerTest.testOpenCashDrawer') }}
      </el-button>
      
      <el-button 
        v-if="isReceiptPrinterMode"
        size="small"
        @click="resetCommand"
      >
        {{ $t('cashDrawerTest.resetToDefault') }}
      </el-button>
    </div>
  </div>
</template>

<style scoped>
.cash-drawer-test {
  padding: 12px;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  background-color: #f9fafb;
}
</style>
